import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@primitives/form";
import { Button } from "@primitives/button";
import { Input } from "@primitives/input";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/useToast";
import { ApiError } from "@/server/apiError";
import {
  changeEmail,
  extractRetryAfter,
  RESEND_COOLDOWN_SECONDS,
} from "@/server/services/emailService";
import { EmailVerificationSchema, EmailVerificationType } from "@/schemas/auth";

interface Props {
  onSuccess: (newEmail: string, initialCooldown: number) => void;
  onUnavailable: () => void;
}

const INLINE_ERRORS = new Set([
  "errors.EmailChange.EmailAlreadyTaken",
  "errors.Email.DisposableDomain",
  "errors.EmailChange.SameAsCurrent",
]);

export default function NewEmailStep({ onSuccess, onUnavailable }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const form = useForm<EmailVerificationType>({
    resolver: zodResolver(EmailVerificationSchema),
    defaultValues: { email: "" },
  });

  async function onSubmit({ email }: EmailVerificationType) {
    try {
      await changeEmail({ newEmail: email });
      onSuccess(email, RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      if (error instanceof ApiError) {
        const code = error.data?.title as string | undefined;

        // BE kill-switch (EMAIL_CHANGE_ENABLED=false) — a 400 from the gate,
        // not a validation failure. Show the unavailable state, not an error.
        if (code === "errors.EmailChange.NotEnabled") {
          onUnavailable();
          return;
        }

        if (code === "errors.EmailVerification.CooldownActive") {
          onSuccess(email, extractRetryAfter(error.data));
          return;
        }

        if (code && INLINE_ERRORS.has(code)) {
          form.setError("email", { message: t(code) });
          return;
        }

        toast({
          description: t([code ?? "errorOccurred", "errorOccurred"]),
          variant: "destructive",
        });
      } else {
        toast({
          description:
            error instanceof Error ? error.message : t("errorOccurred"),
          variant: "destructive",
        });
      }
    }
  }

  return (
    <div className="my-7 space-y-4">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">{t("changeEmail.title")}</h2>
        <p className="text-sm text-muted-foreground">
          {t("changeEmail.description")}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className="rounded-lg border-border bg-card py-6 placeholder:opacity-70"
                    placeholder={t("register.emailStep.emailPlaceholder")}
                    autoComplete="email"
                    type="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="h-12 w-full rounded-lg"
          >
            {form.formState.isSubmitting
              ? t("register.emailStep.sending")
              : t("register.emailStep.continueButton")}
          </Button>
        </form>
      </Form>
    </div>
  );
}
